import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Iconos custom SVG (divIcon) — sin depender de assets externos
const bikeIcon = L.divIcon({
  className: "trilateral-marker",
  html: `<div style="background:#FF3366;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);width:40px;height:40px;border-radius:50%;display:flex;align-items:center;justify-content:center;">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="18.5" cy="17.5" r="3.5"/><path d="M15 6a1 1 0 100-2 1 1 0 000 2zm-3 11.5V14l-3-3 4-3 2 3h2"/></svg>
  </div>`,
  iconSize: [40, 40], iconAnchor: [20, 20],
});
const shopIcon = L.divIcon({
  className: "trilateral-marker",
  html: `<div style="background:#18181B;border:3px solid #D4FF00;box-shadow:0 4px 12px rgba(0,0,0,0.3);width:36px;height:36px;border-radius:6px;display:flex;align-items:center;justify-content:center;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4FF00" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5M4 9v11h16V9M9 22V12h6v10"/></svg>
  </div>`,
  iconSize: [36, 36], iconAnchor: [18, 18],
});
const homeIcon = L.divIcon({
  className: "trilateral-marker",
  html: `<div style="background:#10B981;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l9-8 9 8v10a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1V11z"/></svg>
  </div>`,
  iconSize: [36, 36], iconAnchor: [18, 18],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    const valid = points.filter(p => p && p.lat && p.lng);
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(valid.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

async function fetchOSRMRoute(coords) {
  // coords: [{lat,lng},...] — devuelve {line:[[lat,lng],...], duration:s, distance:m} o null
  const valid = coords.filter(c => c && c.lat && c.lng);
  if (valid.length < 2) return null;
  const path = valid.map(c => `${c.lng},${c.lat}`).join(";");
  try {
    const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${path}?overview=full&geometries=geojson`);
    if (!res.ok) return null;
    const data = await res.json();
    const route = data.routes?.[0];
    if (!route) return null;
    const line = (route.geometry?.coordinates || []).map(([lng, lat]) => [lat, lng]);
    return { line, duration: route.duration, distance: route.distance };
  } catch { return null; }
}

function formatEta(seconds) {
  if (!seconds || seconds < 0) return null;
  const m = Math.max(1, Math.round(seconds / 60));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return `${h}h ${rest}m`;
}

function formatDist(meters) {
  if (!meters) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

/**
 * TrilateralMap
 * Props: driver{lat,lng,nombre,vehiculo}, stores[{lat,lng,nombre}], buyer{lat,lng,nombre,direccion}, height="h-80"
 */
export default function TrilateralMap({ driver, stores = [], buyer, height = "h-80" }) {
  const [routeCoords, setRouteCoords] = useState([]);
  const [eta, setEta] = useState(null); // {duration, distance}
  const validStores = useMemo(() => (stores || []).filter(s => s.lat && s.lng), [stores]);
  const points = useMemo(() => [driver, ...validStores, buyer].filter(Boolean), [driver, validStores, buyer]);

  useEffect(() => {
    let alive = true;
    const seq = [driver, ...validStores, buyer].filter(p => p && p.lat && p.lng);
    if (seq.length >= 2) {
      fetchOSRMRoute(seq).then(r => {
        if (!alive) return;
        if (r) {
          setRouteCoords(r.line);
          setEta({ duration: r.duration, distance: r.distance });
        } else {
          setRouteCoords([]);
          setEta(null);
        }
      });
    } else {
      setRouteCoords([]);
      setEta(null);
    }
    return () => { alive = false; };
  }, [driver?.lat, driver?.lng, validStores.length, buyer?.lat, buyer?.lng]); // eslint-disable-line

  if (points.length === 0) {
    return (
      <div data-testid="trilateral-map-empty" className={`${height} w-full bg-zinc-100 grid place-items-center text-zinc-400 text-sm`}>
        Esperando coordenadas GPS…
      </div>
    );
  }

  const center = points[0];
  const etaMin = formatEta(eta?.duration);
  const etaDist = formatDist(eta?.distance);

  return (
    <div data-testid="trilateral-map" className={`${height} w-full relative`}>
      {/* ETA en vivo overlay */}
      {etaMin && driver && driver.lat && driver.lng && (
        <div data-testid="eta-badge" className="absolute top-3 right-3 z-[1000] bg-zinc-900 text-white rounded-sm px-3 py-2 shadow-lg flex items-center gap-2 pointer-events-none">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4FF00] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#D4FF00]" />
          </span>
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-wider text-white/60">Llega en</div>
            <div className="font-display font-black text-lg tracking-tighter text-[#D4FF00]" data-testid="eta-min">{etaMin}</div>
          </div>
          {etaDist && (
            <div className="border-l border-white/20 pl-2 leading-tight">
              <div className="text-[10px] uppercase tracking-wider text-white/60">Distancia</div>
              <div className="font-display font-bold text-sm tracking-tight">{etaDist}</div>
            </div>
          )}
        </div>
      )}

      <MapContainer center={[center.lat, center.lng]} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {driver && driver.lat && driver.lng && (
          <Marker position={[driver.lat, driver.lng]} icon={bikeIcon}>
            <Popup>
              <b>Repartidor</b><br />{driver.nombre || "En camino"}<br />
              {driver.vehiculo}
              {etaMin && <><br /><b className="text-[#FF3366]">ETA: {etaMin}</b>{etaDist && ` · ${etaDist}`}</>}
            </Popup>
          </Marker>
        )}
        {validStores.map((s, i) => (
          <Marker key={s.id || i} position={[s.lat, s.lng]} icon={shopIcon}>
            <Popup><b>Tienda</b><br />{s.nombre}<br /><span className="text-xs">{s.direccion}</span></Popup>
          </Marker>
        ))}
        {buyer && buyer.lat && buyer.lng && (
          <Marker position={[buyer.lat, buyer.lng]} icon={homeIcon}>
            <Popup><b>Cliente</b><br />{buyer.nombre}<br /><span className="text-xs">{buyer.direccion}</span></Popup>
          </Marker>
        )}
        {routeCoords.length > 0 && (
          <Polyline positions={routeCoords} pathOptions={{ color: "#FF3366", weight: 5, opacity: 0.85 }} />
        )}
      </MapContainer>
    </div>
  );
}
